import { z } from "zod";

/**
 * Creates a `z.enum` and a typed `OPTIONS` array from a single source-of-truth
 * array of `{ value, label }` entries.
 *
 * @example
 * const { schema: fooEnum, options: FOO_OPTIONS } = enumWithOptions([
 *   { value: "a", label: "Option A" },
 *   { value: "b", label: "Option B" },
 * ] as const);
 */
function enumWithOptions<
  const T extends ReadonlyArray<{ readonly value: string; readonly label: string }>,
>(entries: T) {
  const values = entries.map(e => e.value) as [T[number]["value"], ...T[number]["value"][]];
  return {
    schema: z.enum(values),
    options: entries as ReadonlyArray<{ readonly value: T[number]["value"]; readonly label: string }>,
  };
}

export const AttributeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  labels: z.record(z.coerce.number(), z.string()).optional(),
  category: z.string().optional(),
});

export const VersionConfigSchema = z.object({
  attributeIds: z.array(z.string()),
  minRating: z.number().int().default(1),
  maxRating: z.number().int(),
  labelInterval: z.number().min(1).default(1),
}).refine((data) => data.minRating < data.maxRating, {
  message: "minRating must be less than maxRating",
}).refine((data) => {
  if (data.labelInterval === 1) return true;
  return (data.maxRating - data.minRating) % data.labelInterval === 0;
}, {
  message: "(maxRating - minRating) must be a multiple of labelInterval",
});

export const RatingExportSchema = z.object({
  name: z.string().optional(),
  version: z.string().min(1),
  color: z.string().optional(),
  ratings: z.record(z.string(), z.number().int().nullable()),
}).strict();

const { schema: communityIdentityEnum, options: IDENTITY_OPTIONS } = enumWithOptions([
  { value: "ab",                label: "Adult Baby (AB)" },
  { value: "dl",                label: "Diaper Lover (DL)" },
  { value: "little",            label: "Little" },
  { value: "middle",            label: "Middle" },
  { value: "big",               label: "Big / Caregiver" },
  { value: "babyfur",           label: "Babyfur" },
  { value: "kinkster",          label: "Kinkster" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
] as const);
export { IDENTITY_OPTIONS };

const { schema: communityTenureEnum, options: TENURE_OPTIONS } = enumWithOptions([
  { value: "less-than-1",       label: "Less than 1 year" },
  { value: "1-3",               label: "1 – 3 years" },
  { value: "4-10",              label: "4 – 10 years" },
  { value: "10+",               label: "10+ years" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
] as const);
export { TENURE_OPTIONS };

const { schema: ageEnum, options: AGE_OPTIONS } = enumWithOptions([
  { value: "18-24",             label: "18 – 24" },
  { value: "25-34",             label: "25 – 34" },
  { value: "35-44",             label: "35 – 44" },
  { value: "45-54",             label: "45 – 54" },
  { value: "55-64",             label: "55 – 64" },
  { value: "65+",               label: "65+" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
] as const);
export { AGE_OPTIONS };

const { schema: genderEnum, options: GENDER_OPTIONS } = enumWithOptions([
  { value: "man",               label: "Man" },
  { value: "woman",             label: "Woman" },
  { value: "non-binary",        label: "Non-binary / genderqueer" },
  { value: "genderfluid",       label: "Genderfluid" },
  { value: "self-describe",     label: "Prefer to self-describe" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
] as const);
export { GENDER_OPTIONS };

const { schema: transgenderEnum, options: TRANSGENDER_OPTIONS } = enumWithOptions([
  { value: "yes",               label: "Yes" },
  { value: "no",                label: "No" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
] as const);
export { TRANSGENDER_OPTIONS };

export const DemographicsSchema = z.object({
  communityIdentity: z.array(communityIdentityEnum).min(1),
  communityTenure: communityTenureEnum,
  age: ageEnum,
  gender: genderEnum,
  genderSelfDescribe: z.string().optional(),
  transgender: transgenderEnum,
}).strict();

export type Demographics = z.infer<typeof DemographicsSchema>;

export const RatingsSubmissionSchema = RatingExportSchema.extend({
  demographics: DemographicsSchema,
});

export type RatingsSubmission = z.infer<typeof RatingsSubmissionSchema>;

const RatingConfigObject = z.object({
  currentVersion: z.string(),
  versions: z.record(z.string(), VersionConfigSchema),
  defaultLabels: z.record(z.coerce.number(), z.string()),
  attributes: z.array(AttributeSchema),
  themeSet: z.enum(["default", "custom"]).optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  nameLabel: z.string().optional(),
  attributeTerm: z.string().optional(),
  attributeTermPlural: z.string().optional(),
  datasetColors: z.array(z.string()).min(1).optional(),
}).strict();

function validateConfigRefs(data: {
  currentVersion: string;
  versions: Record<string, { attributeIds: string[] }>;
  attributes: { id: string }[];
}): boolean {
  if (!(data.currentVersion in data.versions)) return false;
  const validIds = new Set(data.attributes.map(a => a.id));
  for (const v of Object.values(data.versions)) {
    if (!v.attributeIds.every(id => validIds.has(id))) return false;
  }
  return true;
}

const CONFIG_REFINE_OPTIONS = {
  message: "Invalid configuration: missing version or attribute ID references.",
} as const;

export const RatingConfigSchema = RatingConfigObject
  .refine(validateConfigRefs, CONFIG_REFINE_OPTIONS);

export const ServerRatingConfigSchema = RatingConfigObject
  .extend({ trustedProxies: z.array(z.string()).optional() })
  .refine(validateConfigRefs, CONFIG_REFINE_OPTIONS);
